/**
 * The analysis' own yield per panel on each analysed roof: the mean annual
 * kWh of the analysis' panels on that face, else the whole-roof mean. Null
 * when the analysis has no panels, or the figure for a face is not positive.
 *
 * The designer shows this as the "Satellite estimate" for a repacked layout,
 * and the server stores it on repacked panels when a design is saved, so both
 * sides read the same number from the same analysis. It ignores tilt-frame
 * mounting: PVWatts or the MCS calculation own that.
 */
export const analysisYieldPerPanelByFace = (
  analysisPanels:
    | ReadonlyArray<{ faceIndex: number; annualAcKwh: number }>
    | null
    | undefined
): ((faceId: number) => number | null) | null => {
  if (!analysisPanels?.length) return null;
  const byFace = new Map<number, { sum: number; n: number }>();
  let total = 0;
  for (const panel of analysisPanels) {
    total += panel.annualAcKwh;
    const entry = byFace.get(panel.faceIndex) ?? { sum: 0, n: 0 };
    entry.sum += panel.annualAcKwh;
    entry.n += 1;
    byFace.set(panel.faceIndex, entry);
  }
  const roofMean = total / analysisPanels.length;
  return (faceId) => {
    const entry = byFace.get(faceId);
    const perPanel = entry ? entry.sum / entry.n : roofMean;
    return perPanel > 0 ? perPanel : null;
  };
};

/**
 * A kept analysed roof (not newly drawn) that the analysis does not index,
 * whose generation record is missing or "unavailable": typically one kept
 * through Keep my layout whose record a save lost, e.g. an undone Start over
 * that autosaved an empty layout in between. It is still a repacked analysed
 * roof, so it carries the analysis' whole-roof yield per panel. Shared by the
 * designer's satellite estimate and the server's save verification.
 */
export const solarAnalysedRoofLostRecord = (
  face: {
    isNew: boolean;
    appliedArrayAzimuthDegrees?: number | null;
    generation?: { source: string } | null;
  },
  analysis: { indexesFace: boolean; hasPanels: boolean }
): boolean =>
  !face.isNew &&
  !analysis.indexesFace &&
  analysis.hasPanels &&
  face.appliedArrayAzimuthDegrees !== null &&
  (!face.generation || face.generation.source === 'unavailable');
